/**
 * Authentication and Payment API service
 * This file contains methods for communicating with the backend API
 * for authentication and payment processing
 */

const API_BASE_URL = "http://localhost:5001/api";

/**
 * Process Google sign-in through the backend
 * @param {string} credential - Google credential token
 * @returns {Promise<Object>} User data
 */
export const authenticateWithGoogle = async (credential) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/google`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ credential }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Google authentication failed");
    }

    const userData = await response.json();

    // Save token to localStorage
    if (userData && userData.token) {
      localStorage.setItem("token", userData.token);
    }

    return userData;
  } catch (error) {
    console.error("Google authentication error:", error);
    throw error;
  }
};

/**
 * Authenticate with email and password through the backend
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} User data
 */
export const authenticateWithEmail = async (email, password) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/email`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Email authentication failed");
    }

    const userData = await response.json();

    // Save token to localStorage
    if (userData && userData.token) {
      localStorage.setItem("token", userData.token);
    }

    return userData;
  } catch (error) {
    console.error("Email authentication error:", error);
    throw error;
  }
};

/**
 * Get the current user data using JWT token
 * @returns {Promise<Object>} User data
 */
export const getCurrentUser = async () => {
  try {
    const token = localStorage.getItem("token");

    if (!token) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to get user data");
    }

    return await response.json();
  } catch (error) {
    console.error("Get current user error:", error);
    throw error;
  }
};

/**
 * Create a Stripe checkout session through the backend
 * @param {Object} planData - Data about the selected plan
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Checkout session data including the checkout URL
 */
export const createStripeCheckoutSession = async (planData, userId) => {
  try {
    const token = localStorage.getItem("token");

    const response = await fetch(
      `${API_BASE_URL}/payments/create-checkout-session`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          planData,
          userId,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create checkout session");
    }

    return await response.json();
  } catch (error) {
    console.error("Stripe checkout error:", error);
    throw error;
  }
};

/**
 * Verify payment success and update user credits
 * @param {string} sessionId - Stripe session ID
 * @returns {Promise<Object>} Updated user data
 */
export const verifyPaymentSuccess = async (sessionId) => {
  try {
    const token = localStorage.getItem("token");

    const response = await fetch(`${API_BASE_URL}/payments/verify-payment`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: JSON.stringify({ sessionId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to verify payment");
    }

    return await response.json();
  } catch (error) {
    console.error("Payment verification error:", error);
    throw error;
  }
};

/**
 * Get current user credits
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User credits data
 */
export const getUserCredits = async (userId) => {
  try {
    const token = localStorage.getItem("token");

    const response = await fetch(`${API_BASE_URL}/users/${userId}/credits`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch user credits");
    }

    return await response.json();
  } catch (error) {
    console.error("Get user credits error:", error);
    throw error;
  }
};
